import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import { Plus, TrendingUp, TrendingDown, Scale, ArrowRight, Trash2, CheckCircle2, FileEdit, ChevronRight, Calendar, Layers } from 'lucide-react';
import { api, salApi } from '../../lib/api';
import { formatCurrency, isZeroMoney, moneySign } from '../../lib/utils';
import { isExtraContractEffective } from '../../lib/extraEligibility';
import { ExtraSummaryDialog } from '../extras/ExtraSummaryDialog';
import { toast } from 'sonner';

const num = (v) => (typeof v === 'number' ? v : parseFloat(v) || 0);
const fmtDate = (d) => { try { return d ? new Date(d).toLocaleDateString('it-IT') : null; } catch { return null; } };

// Causali "in diminuzione" — etichette semplici mappate al dominio interno.
const CAUSALI = [
  { value: 'rinuncia_cliente', label: 'Rinuncia del cliente' },
  { value: 'impossibilita_tecnica', label: 'Impossibilità tecnica' },
  { value: 'errore_progettuale', label: 'Errore di progetto' },
  { value: 'altro', label: 'Altro' },
];

/**
 * Superficie unificata "Extra / Variazioni Lavori".
 * (+) Lavorazione aggiuntiva → dominio Extra esistente (wizard).
 * (−) Lavorazione in diminuzione → workflow BOZZA → APPROVATA. La bozza NON riduce
 *     il perimetro; l'approvazione applica lo Stralcio R.5 contract_reduction (authoritative).
 * Riepilogo economico = source-of-truth backend (sal-contract-summary).
 */
export const ExtraVariazioniPanel = ({ projectId, extras = [], salItems = [], canEdit = false, canViewEconomic = true, projectClosed = false, onNewExtra, onOpenExtra, onChanged, hideSummary = false, hideCta = false, titleText = 'Storico variazioni', hiddenExtraIds = null, hiddenNegativeIds = null }) => {
  const [summary, setSummary] = useState(null);
  const [variations, setVariations] = useState([]);
  const [chooseOpen, setChooseOpen] = useState(false);
  const [negOpen, setNegOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [causale, setCausale] = useState('rinuncia_cliente');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [approveFor, setApproveFor] = useState(null);
  const [detail, setDetail] = useState(null); // { kind:'extra'|'variation'|'legacy', data }

  const loadSummary = useCallback(async () => {
    try { const res = await salApi.getContractSummary(projectId); setSummary(res.data); }
    catch (e) { setSummary(null); }
  }, [projectId]);

  const loadVariations = useCallback(async () => {
    try { const res = await api.get(`/projects/${projectId}/negative-variations`); setVariations(res.data || []); }
    catch (e) { setVariations([]); }
  }, [projectId]);

  useEffect(() => { loadSummary(); loadVariations(); }, [loadSummary, loadVariations, salItems, extras]);

  const refresh = () => { onChanged && onChanged(); loadSummary(); loadVariations(); };

  const reducibleItems = (salItems || []).filter(
    (i) => (i.source || 'contratto') === 'contratto'
      && (i.source_type || 'lavoro') === 'lavoro'
      && i.item_status !== 'non_eseguita'
  );

  const positives = (extras || []).filter((e) => num(e.total) >= 0);
  const isApproved = (e) => isExtraContractEffective(e);

  const selected = reducibleItems.find((i) => i.id === selectedItemId);
  const itemById = (id) => (salItems || []).find((i) => i.id === id);
  const residualOf = (it) => {
    const c = num(it?.total_quantity); const e = num(it?.completed_quantity);
    return { contract: c, executed: e, residualQty: Math.max(0, c - e), unitPrice: num(it?.unit_price) };
  };
  const sel = residualOf(selected);
  const selResidualValue = sel.residualQty * sel.unitPrice;

  const startNegative = () => { setChooseOpen(false); setNegOpen(true); };
  const startExtra = (type) => { setChooseOpen(false); onNewExtra && onNewExtra(type); };

  const saveBozza = async () => {
    if (!selectedItemId) { toast.error('Seleziona la voce da ridurre'); return; }
    if ((reason || '').trim().length < 8) { toast.error('Motivazione obbligatoria (min 8 caratteri)'); return; }
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/negative-variations`, { sal_item_id: selectedItemId, reason: reason.trim(), category: causale });
      toast.success('Bozza di diminuzione salvata (non ancora efficace)');
      setNegOpen(false); setSelectedItemId(''); setReason(''); setCausale('rinuncia_cliente');
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === 'object' && d?.message) ? d.message : (d || 'Errore salvataggio bozza'));
    } finally { setBusy(false); }
  };

  const approveNow = async () => {
    if (!approveFor) return;
    setBusy(true);
    try {
      await api.post(`/negative-variations/${approveFor.id}/approve`);
      toast.success('Diminuzione approvata: perimetro e SAL aggiornati');
      setApproveFor(null);
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === 'object' && d?.message) ? d.message : (d || 'Errore approvazione'));
    } finally { setBusy(false); }
  };

  const deleteBozza = async (v) => {
    setBusy(true);
    try { await api.delete(`/negative-variations/${v.id}`); toast.success('Bozza eliminata'); refresh(); }
    catch (e) { const d = e?.response?.data?.detail; toast.error((typeof d === 'object' && d?.message) ? d.message : (d || 'Errore eliminazione')); }
    finally { setBusy(false); }
  };

  const posSum = num(summary?.positive_variations);
  const negSum = num(summary?.standalone_stralci_total) + num(summary?.negative_variations_other_than_stralci);
  const saldoVar = posSum - negSum;
  const approveItem = approveFor ? residualOf(itemById(approveFor.sal_item_id)) : null;

  // ===== Lista unificata compatta (Extra +/− + Diminuzioni + Stralci legacy) =====
  const EXTRA_STATUS_LABEL = {
    bozza: 'Bozza', richiesto: 'Richiesto', in_preparazione: 'In preparazione', pronto: 'Pronto',
    in_attesa_firma: 'In attesa firma', firmato_cliente: 'Firmato', firmato: 'Firmato',
    in_validazione: 'In validazione', validato: 'Validato', approvato: 'Approvato',
    approvato_admin: 'Approvato', accettato_cliente: 'Accettato', ordinato: 'Ordinato',
    completato: 'Completato', rifiutato: 'Rifiutato',
  };
  // Stati pre-firma (approvazione interna): se esiste il file firmato dal cliente
  // (signed_pdf_url + signed_at) la label deve mostrare "Firmato" (solo mapping visuale,
  // nessuna modifica alla state machine né alla logica di fatturabilità).
  const PRE_SIGN_STATUSES = new Set([
    'bozza', 'richiesto', 'in_preparazione', 'pronto', 'in_attesa_firma',
    'approvato', 'approvato_admin', 'in_attesa_approvazione',
  ]);
  const extraStatoLabel = (e) => {
    const base = EXTRA_STATUS_LABEL[e.approval_status] || e.approval_status || '—';
    const signed = !!(e.signed_at || e.signed_pdf_url);
    if (signed && PRE_SIGN_STATUSES.has(e.approval_status)) return 'Firmato';
    return base;
  };
  const variationItemIds = new Set((variations || []).map((v) => v.sal_item_id));
  const hiddenE = hiddenExtraIds instanceof Set ? hiddenExtraIds : new Set(hiddenExtraIds || []);
  const hiddenN = hiddenNegativeIds instanceof Set ? hiddenNegativeIds : new Set(hiddenNegativeIds || []);
  const positivesVisible = positives.filter((e) => !hiddenE.has(e.id));
  const variationsVisible = (variations || []).filter((v) => !hiddenN.has(v.id));
  const legacyStralci = (salItems || []).filter(
    (i) => i.item_status === 'non_eseguita' && !i.stralcio_reverted
      && (i.economic_effect || 'contract_reduction') === 'contract_reduction'
      && !variationItemIds.has(i.id)
  );
  const rows = [
    ...positivesVisible.map((e) => ({
      key: `e-${e.id}`, code: e.extra_number || '—', desc: e.title || 'Extra',
      tipo: e.extra_type === 'fornitura' ? 'Extra fornitura' : 'Extra lavori', tone: 'emerald',
      stato: extraStatoLabel(e),
      statoTone: isApproved(e) ? 'green' : 'amber', sign: '+', amount: num(e.total), kind: 'extra', data: e,
      count: (e.items || []).length, date: fmtDate(e.signed_at || e.approved_at || e.effective_at || e.created_at),
    })),
    ...variationsVisible.map((v) => ({
      key: `v-${v.id}`, code: v.item_code || '—', desc: `Riduzione ${v.item_description || ''}`.trim(),
      tipo: 'Diminuzione', tone: 'red', stato: v.status === 'bozza' ? 'Bozza' : 'Approvata',
      statoTone: v.status === 'bozza' ? 'amber' : 'red', sign: '−',
      amount: num(v.applied_stralcio_amount) || num(v.value), kind: 'variation', data: v,
      count: 0, date: fmtDate(v.applied_at || v.created_at),
    })),
    ...legacyStralci.map((i) => ({
      key: `l-${i.id}`, code: i.code || '—', desc: `Riduzione ${i.description || ''}`.trim(),
      tipo: 'Diminuzione', tone: 'red', stato: 'Legacy · applicata', statoTone: 'gray', sign: '−',
      amount: num(i.stralcio_amount) || num(i.total_value), kind: 'legacy', data: i,
      count: 0, date: null,
    })),
  ];
  const toneBadge = { green: 'bg-emerald-100 text-emerald-700', amber: 'bg-amber-100 text-amber-700', red: 'bg-red-100 text-red-700', gray: 'bg-slate-100 text-slate-600' };

  return (
    <div className="space-y-4" data-testid="extra-variazioni-panel">
      {/* Riepilogo economico (source-of-truth backend) — gated per ruoli senza accesso economico */}
      {summary && canViewEconomic && !hideSummary && (
        <Card className="card-abitalo overflow-hidden">
          <CardContent className="p-0">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-slate-100">
              <div className="p-4" data-testid="summary-contratto-originario">
                <p className="text-xs text-[#737373]">Contratto originario</p>
                <p className="text-lg font-semibold text-[#171717]">{formatCurrency(num(summary.initial_contract_value))}</p>
              </div>
              <div className="p-4" data-testid="summary-variazioni-positive">
                <p className="text-xs text-emerald-600 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Variazioni positive</p>
                <p className="text-lg font-semibold text-emerald-700">{isZeroMoney(posSum) ? '' : '+'}{formatCurrency(posSum)}</p>
              </div>
              <div className="p-4" data-testid="summary-variazioni-negative">
                <p className="text-xs text-red-500 flex items-center gap-1"><TrendingDown className="w-3.5 h-3.5" /> Variazioni negative</p>
                <p className="text-lg font-semibold text-red-600">{isZeroMoney(negSum) ? '' : '−'}{formatCurrency(negSum)}</p>
              </div>
              <div className="p-4 bg-[#FFF7F0]" data-testid="summary-valore-vigente">
                <p className="text-xs text-[#FF7819] flex items-center gap-1"><Scale className="w-3.5 h-3.5" /> Valore lavori vigente</p>
                <p className="text-lg font-bold text-[#FF7819]">{formatCurrency(num(summary.current_contract_value_computed))}</p>
              </div>
            </div>
            <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-sm text-[#525252] flex items-center gap-2" data-testid="summary-saldo-variazioni">
              <span>Saldo variazioni:</span>
              <span className={`font-semibold ${saldoVar >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                {moneySign(saldoVar)}{formatCurrency(Math.abs(saldoVar))}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {(titleText || (canEdit && !projectClosed && !hideCta)) && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h4 className="text-sm font-medium text-[#737373]">{titleText}</h4>
          {canEdit && !projectClosed && !hideCta && (
            <Button onClick={() => setChooseOpen(true)} data-testid="new-variazione-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuovo Extra / Variazione
            </Button>
          )}
        </div>
      )}

      {/* Lista card leggibili */}
      <div data-testid="variazioni-table">
        {rows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#E4E4E4] bg-white px-4 py-3 text-sm text-[#999] italic" data-testid="variazioni-empty">
            Nessun Extra o diminuzione fuori variante.
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="variazioni-cards">
            {rows.map((r) => (
              <button
                key={r.key}
                data-testid={`variazione-row-${r.data.id}`}
                onClick={() => setDetail({ kind: r.kind, data: r.data })}
                className="w-full text-left rounded-xl border border-[#ECECEC] bg-white p-4 hover:border-[#FF7819] hover:shadow-sm transition-colors flex items-start justify-between gap-4"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#171717]">{r.code}</span>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${toneBadge[r.statoTone]}`} data-testid={`variazione-status-${r.data.id}`}>{r.stato}</span>
                  </div>
                  <p className="text-sm text-[#525252] mt-1 break-words">{r.desc}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-[#8a8a8a] flex-wrap">
                    <span className={`inline-flex items-center gap-1 font-medium ${r.tone === 'red' ? 'text-red-600' : 'text-emerald-700'}`}>
                      {r.tone === 'red' ? <TrendingDown className="w-3.5 h-3.5" /> : <TrendingUp className="w-3.5 h-3.5" />} {r.tipo}
                    </span>
                    {r.count > 0 && <span className="inline-flex items-center gap-1"><Layers className="w-3 h-3" /> {r.count} {r.count === 1 ? 'lavorazione' : 'lavorazioni'}</span>}
                    {r.date && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {r.date}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className={`text-xl font-bold whitespace-nowrap ${r.sign === '−' ? 'text-red-600' : 'text-emerald-700'}`}>{isZeroMoney(r.amount) ? '' : r.sign + ' '}{formatCurrency(r.amount)}</p>
                  <ChevronRight className="w-4 h-4 text-[#c4c4c4] ml-auto mt-1" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Dialog scelta tipo */}
      <Dialog open={chooseOpen} onOpenChange={setChooseOpen}>
        <DialogContent data-testid="variazione-choose-dialog">
          <DialogHeader>
            <DialogTitle>Cosa vuoi inserire?</DialogTitle>
            <DialogDescription>Il capitolato contrattuale resta invariato. Registra una variazione rispetto al lavoro contrattualizzato.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <button onClick={() => startExtra('lavoro')} data-testid="variazione-tipo-extra-lavoro"
              className="flex items-center justify-between p-4 rounded-lg border-2 border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50 transition-colors text-left">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
                <div>
                  <p className="font-medium">Extra lavori (+)</p>
                  <p className="text-xs text-[#737373]">Lavorazione aggiuntiva</p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-[#a3a3a3]" />
            </button>
            <button onClick={() => startExtra('fornitura')} data-testid="variazione-tipo-extra-fornitura"
              className="flex items-center justify-between p-4 rounded-lg border-2 border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50 transition-colors text-left">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
                <div>
                  <p className="font-medium">Extra fornitura (+)</p>
                  <p className="text-xs text-[#737373]">Fornitura aggiuntiva</p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-[#a3a3a3]" />
            </button>
            <button onClick={startNegative} data-testid="variazione-tipo-negativa"
              className="flex items-center justify-between p-4 rounded-lg border-2 border-red-200 hover:border-red-400 hover:bg-red-50 transition-colors text-left">
              <div className="flex items-center gap-3">
                <TrendingDown className="w-6 h-6 text-red-500" />
                <div>
                  <p className="font-medium">Lavorazione in diminuzione (−)</p>
                  <p className="text-xs text-[#737373]">Riduzione del lavoro contrattualizzato</p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-[#a3a3a3]" />
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog crea bozza diminuzione */}
      <Dialog open={negOpen} onOpenChange={(o) => !o && setNegOpen(false)}>
        <DialogContent data-testid="variazione-negativa-dialog">
          <DialogHeader>
            <DialogTitle>Lavorazione in diminuzione</DialogTitle>
            <DialogDescription>Crea una bozza. Diventerà efficace sul perimetro dei lavori solo dopo l'approvazione.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">Voce contrattuale da ridurre *</label>
              <Select value={selectedItemId} onValueChange={setSelectedItemId}>
                <SelectTrigger data-testid="variazione-negativa-voce"><SelectValue placeholder="Seleziona una voce" /></SelectTrigger>
                <SelectContent>
                  {reducibleItems.map((i) => (
                    <SelectItem key={i.id} value={i.id}>{i.code} · {i.description?.slice(0, 50)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selected && (
              <div className="grid grid-cols-3 gap-2 text-sm bg-[#FAFAFA] rounded-lg p-3" data-testid="variazione-negativa-residuo">
                <div><p className="text-[11px] text-[#737373]">Contrattuale</p><p className="font-medium">{sel.contract.toFixed(2)} {selected.unit}</p></div>
                <div><p className="text-[11px] text-[#737373]">Già eseguita</p><p className="font-medium">{sel.executed.toFixed(2)} {selected.unit}</p></div>
                <div><p className="text-[11px] text-[#737373]">Residua</p><p className="font-medium text-red-600">{sel.residualQty.toFixed(2)} {selected.unit}</p></div>
                <div className="col-span-2"><p className="text-[11px] text-[#737373]">Prezzo unitario</p><p className="font-medium">{formatCurrency(sel.unitPrice)}</p></div>
                <div><p className="text-[11px] text-[#737373]">Valore diminuzione</p><p className="font-semibold text-red-600">{isZeroMoney(selResidualValue) ? '' : '−'}{formatCurrency(selResidualValue)}</p></div>
              </div>
            )}

            <div>
              <label className="text-xs font-medium">Causale</label>
              <Select value={causale} onValueChange={setCausale}>
                <SelectTrigger data-testid="variazione-negativa-causale"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CAUSALI.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium">Motivazione *</label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                data-testid="variazione-negativa-reason" placeholder="Descrivi la diminuzione (min 8 caratteri)" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNegOpen(false)}>Annulla</Button>
            <Button onClick={saveBozza} disabled={busy} data-testid="variazione-negativa-confirm"
              className="bg-[#FF7819] hover:bg-[#E56A0F]"><FileEdit className="w-4 h-4 mr-1" /> Salva bozza</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma approvazione */}
      <Dialog open={!!approveFor} onOpenChange={(o) => !o && setApproveFor(null)}>
        <DialogContent data-testid="variazione-approva-dialog">
          <DialogHeader>
            <DialogTitle>Approva lavorazione in diminuzione</DialogTitle>
            <DialogDescription>L'approvazione aggiornerà il perimetro lavori vigente e il relativo avanzamento SAL.</DialogDescription>
          </DialogHeader>
          {approveFor && approveItem && (
            <div className="space-y-2 text-sm bg-[#FAFAFA] rounded-lg p-3">
              <div className="flex justify-between"><span className="text-[#737373]">Voce</span><span className="font-medium">{approveFor.item_code} · {approveFor.item_description}</span></div>
              <div className="flex justify-between"><span className="text-[#737373]">Contratto</span><span className="font-medium">{approveItem.contract.toFixed(2)} {approveFor.unit}</span></div>
              <div className="flex justify-between"><span className="text-[#737373]">Già eseguito</span><span className="font-medium">{approveItem.executed.toFixed(2)} {approveFor.unit}</span></div>
              <div className="flex justify-between"><span className="text-[#737373]">Residuo da eliminare</span><span className="font-medium text-red-600">{approveItem.residualQty.toFixed(2)} {approveFor.unit}</span></div>
              <div className="flex justify-between border-t border-slate-200 pt-2"><span className="text-[#737373]">Valore diminuzione</span><span className="font-semibold text-red-600">{isZeroMoney(num(approveFor.value)) ? '' : '−'}{formatCurrency(num(approveFor.value))}</span></div>
              <div className="flex justify-between"><span className="text-[#737373]">Motivazione</span><span className="font-medium text-right max-w-[60%]">{approveFor.reason}</span></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveFor(null)}>Annulla</Button>
            <Button onClick={approveNow} disabled={busy} data-testid="variazione-approva-confirm"
              className="bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="w-4 h-4 mr-1" /> Approva diminuzione</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extra: modal canonico condiviso (stessa UI in tutto il CRM) */}
      <ExtraSummaryDialog
        open={!!detail && detail.kind === 'extra'}
        onOpenChange={(o) => !o && setDetail(null)}
        extra={detail?.kind === 'extra' ? detail.data : null}
        onOpenFull={onOpenExtra ? (id) => { setDetail(null); onOpenExtra(id); } : null}
      />

      {/* Drawer dettaglio variazione/legacy (NON Extra) */}
      <Dialog open={!!detail && detail.kind !== 'extra'} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="variazione-detail-dialog">
          {detail?.kind === 'variation' && (() => {
            const v = detail.data; const it = residualOf(itemById(v.sal_item_id)); const bozza = v.status === 'bozza';
            return (
              <>
                <DialogHeader>
                  <DialogTitle>Lavorazione in diminuzione</DialogTitle>
                  <DialogDescription>{v.item_code} · {v.item_description} · {bozza ? 'Bozza' : 'Approvata'}</DialogDescription>
                </DialogHeader>
                <div className="space-y-2 text-sm bg-[#FAFAFA] rounded-lg p-3">
                  <div className="flex justify-between"><span className="text-[#737373]">Contratto</span><span className="font-medium">{it.contract.toFixed(2)} {v.unit}</span></div>
                  <div className="flex justify-between"><span className="text-[#737373]">Già eseguito</span><span className="font-medium">{it.executed.toFixed(2)} {v.unit}</span></div>
                  <div className="flex justify-between"><span className="text-[#737373]">Residuo</span><span className="font-medium text-red-600">{it.residualQty.toFixed(2)} {v.unit}</span></div>
                  <div className="flex justify-between border-t border-slate-200 pt-2"><span className="text-[#737373]">Valore diminuzione</span><span className="font-semibold text-red-600">{isZeroMoney(num(v.applied_stralcio_amount) || num(v.value)) ? '' : '−'}{formatCurrency(num(v.applied_stralcio_amount) || num(v.value))}</span></div>
                  <div className="flex justify-between"><span className="text-[#737373]">Motivazione</span><span className="font-medium text-right max-w-[60%]">{v.reason}</span></div>
                </div>
                <DialogFooter>
                  {bozza && canEdit && (
                    <>
                      <Button variant="ghost" onClick={() => { const vv = v; setDetail(null); deleteBozza(vv); }} data-testid="variazione-detail-delete" className="text-red-500 hover:bg-red-50 mr-auto"><Trash2 className="w-4 h-4 mr-1" /> Elimina</Button>
                      <Button onClick={() => { const vv = v; setDetail(null); setApproveFor(vv); }} data-testid="variazione-detail-approva" className="bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="w-4 h-4 mr-1" /> Approva</Button>
                    </>
                  )}
                  <Button variant="outline" onClick={() => setDetail(null)}>Chiudi</Button>
                </DialogFooter>
              </>
            );
          })()}

          {detail?.kind === 'legacy' && (() => {
            const i = detail.data;
            return (
              <>
                <DialogHeader>
                  <DialogTitle>Lavorazione in diminuzione</DialogTitle>
                  <DialogDescription>{i.code} · {i.description} · <span className="text-slate-500">Legacy · già applicata</span></DialogDescription>
                </DialogHeader>
                <div className="space-y-2 text-sm bg-[#FAFAFA] rounded-lg p-3">
                  <div className="flex justify-between"><span className="text-[#737373]">Valore ridotto</span><span className="font-semibold text-red-600">{isZeroMoney(num(i.stralcio_amount) || num(i.total_value)) ? '' : '−'}{formatCurrency(num(i.stralcio_amount) || num(i.total_value))}</span></div>
                  {i.exclusion_reason && <div className="flex justify-between"><span className="text-[#737373]">Motivazione</span><span className="font-medium text-right max-w-[60%]">{i.exclusion_reason}</span></div>}
                  <p className="text-xs text-[#a3a3a3] pt-1">Riduzione registrata prima del workflow bozza/approvazione. Gestibile dal tab Avanzamento (riattivazione).</p>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDetail(null)}>Chiudi</Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExtraVariazioniPanel;
